/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Cashback AutoSpending component
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.12.0
 */

import { useCallback, useEffect, useState } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';

import Switch from '../../../../components/common/Switch/Switch';
import ArrowTooltip from '../../../../components/common/Tooltip/Tooltip';
import { IPageCashbackControlTranslation } from '../../../../modules/internationalization/types/pages/cashback';
import ServiceUserDenied from '../../../../modules/role/components/ServiceUserDenied';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { fonts } from '../../../../theme';
import { makeSelectAuthoritiesIsServiceUser, makeSelectIsFeatureWithVersionEnabled } from '../../../PageAuth/selectors/authSelectors';
import { cashbackToggleAutoSpendingStartAction } from '../../actions/cashbackControlActions';
import { CASHBACK_AUTO_RESERVE_ELEMENT_ID } from '../../pageCashbackConstants';
import {
    makeSelectCashbackAutoSpendingAvailable,
    makeSelectCashbackAutoSpendingTooltip,
} from '../../selectors/cashbackControlSelectors';
import { TCashbackToggleAutoSpendingPayload } from '../../types/cashbackControlTypes';
import { CashbackAutoSpendingBonusesStyled } from '../styled/CashbackControlStyled';
import { CashbackTextStyled } from '../styled/CashbackStyled';

export interface ICashbackAutoSpendingBonuses {
    isAutoSpendingEnabled: boolean;
    serviceListEmpty: boolean;
    tabControlTranslation: IPageCashbackControlTranslation;
}

const CashbackAutoSpendingBonuses = (props: ICashbackAutoSpendingBonuses & IConnectedProps & IDispatchToProps) => {

    const {
        autoSpendingTooltip,
        isAutoSpendingEnabled,
        isActionAutoSpendingAvailable,
        isServiceUser,
        tabControlTranslation,
        serviceListEmpty,

        toggleAutoSpending,
    } = props;
    const isCashbackV4Enabled = useSelector(makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CASHBACK, [4], true));

    // Доступность самого переключателя (не его текущее состояние)
    const [isAutoSpendingAvailable, setAutoSpendingAvailable] = useState<boolean>(true);

    useEffect(() => {
        setAutoSpendingAvailable((!serviceListEmpty || isCashbackV4Enabled) && isActionAutoSpendingAvailable);
    }, [serviceListEmpty, isActionAutoSpendingAvailable, isCashbackV4Enabled]);

    /*
    * -------------------------------------------
    *               Render
    * -------------------------------------------
    */

    const renderSwitch = useCallback((checked: boolean, disabled: boolean) => (
        <div
            className="auto-spending-bonuses__switch"
            onClick={() => toggleAutoSpending({ isAutoSpendingEnabled: checked })}
        >
            <Switch checked={checked} data-name="autoSpendingBonusesSwitch" disabled={disabled}/>
            <CashbackTextStyled>
                {tabControlTranslation.autoSpending.switchLabel}
            </CashbackTextStyled>
        </div>
    ), [tabControlTranslation, toggleAutoSpending]);

    return (
        <CashbackAutoSpendingBonusesStyled
            className="cashback-control__auto-spending-bonuses"
            disabled={isServiceUser || !isAutoSpendingAvailable}
            id={CASHBACK_AUTO_RESERVE_ELEMENT_ID}
        >
            <CashbackTextStyled
                className="auto-spending-bonuses__title"
                font={fonts.textHeader3}
            >
                {tabControlTranslation.autoSpending.title}
            </CashbackTextStyled>
            <CashbackTextStyled className="auto-spending-bonuses__subtitle">
                {tabControlTranslation.autoSpending.subTitle}
            </CashbackTextStyled>

            {
                isServiceUser
                    ? <ServiceUserDenied renderComponent={disabled =>
                        renderSwitch(!disabled && isAutoSpendingEnabled, disabled || !isAutoSpendingAvailable)
                    }/>
                    : <ArrowTooltip title={autoSpendingTooltip} titleAlign="center">
                        <div style={{ width: 'fit-content' }}>
                            {renderSwitch(isAutoSpendingEnabled, !isAutoSpendingAvailable)}
                        </div>
                    </ArrowTooltip>
            }
        </CashbackAutoSpendingBonusesStyled>
    );
};

export interface IConnectedProps {
    autoSpendingTooltip: string;
    isActionAutoSpendingAvailable: boolean;
    isServiceUser: boolean;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    autoSpendingTooltip: makeSelectCashbackAutoSpendingTooltip(),
    isActionAutoSpendingAvailable: makeSelectCashbackAutoSpendingAvailable(),
    isServiceUser: makeSelectAuthoritiesIsServiceUser(),
});

export interface IDispatchToProps {
    toggleAutoSpending: (payload: TCashbackToggleAutoSpendingPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchToProps => ({
    toggleAutoSpending: payload => dispatch(cashbackToggleAutoSpendingStartAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CashbackAutoSpendingBonuses);
